import React, { useMemo } from "react";
import { Box, Typography, Badge } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const QuickLinkCard = ({ icon, label, gradient, onClick, badge }) => {
  return (
    <Box
      className="quick-links__card"
      onClick={onClick}
      sx={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: "20px",
        padding: "16px 20px",
        flex: 1,
        minWidth: "140px",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "visible",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "12px",
        "&:hover": {
          transform: "translateY(-6px) scale(1.02)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
          "& .quick-links__icon-container": {
            transform: "scale(1.1) rotate(5deg)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: gradient,
          transform: "scaleX(0)",
          transformOrigin: "left",
          transition: "transform 0.3s ease",
        },
        "&:hover::after": {
          transform: "scaleX(1)",
        },
      }}
    >
      <Badge
        overlap="circular"
        showZero
        badgeContent={badge}
        sx={{
          "& .MuiBadge-badge": {
            background: "#c26229",
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.7rem",
            minWidth: "20px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          },
        }}
      >
        <Box
          className="quick-links__icon-container"
          sx={{
            width: "48px",
            height: "48px",
            minWidth: "48px",
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.3s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
          }}
        >
          {icon}
        </Box>
      </Badge>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          color: "#333",
          fontSize: "0.9rem",
          margin: 0,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const QuickLinks = ({ side, onCreateTask }) => {
  const navigate = useNavigate();
  const taskSet = useSelector((state) => state.tasks.taskSet);
  const taskCount = useMemo(
    () => Object.values(taskSet).filter((t) => !t.completed).length,
    [taskSet],
  );

  const links = [
    {
      icon: <AssignmentIcon sx={{ color: "#fff", fontSize: "28px" }} />,
      label: "My Tasks",
      gradient: "linear-gradient(135deg, #197B68 0%, #318b79 100%)",
      badge: taskCount,
      onClick: () => navigate("/tasks"),
    },
    {
      icon: <CalendarMonthIcon sx={{ color: "#fff", fontSize: "28px" }} />,
      label: "Plan Day",
      gradient: "linear-gradient(135deg, #276580 0%, #3f7891 100%)",
      onClick: () => navigate("/planner"),
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: "28px" }} />,
      label: "Completed",
      gradient: "linear-gradient(135deg, #429254 0%, #5ab06a 100%)",
      onClick: () => navigate("/completed-tasks"),
    },
    {
      icon: <AddCircleOutlineIcon sx={{ color: "#fff", fontSize: "28px" }} />,
      label: "New Task",
      gradient: "linear-gradient(135deg, #813773 0%, #a04990 100%)",
      onClick: onCreateTask || (() => navigate("/create-task")),
    },
  ];

  // When side is specified, reorder links accordingly
  // Left: My Tasks, New Task
  // Right: Plan Day, Completed
  // No side (default): all 4 in original order
  const displayLinks =
    side === "left"
      ? [links[0], links[3]]
      : side === "right"
        ? [links[1], links[2]]
        : links;

  return (
    <Box
      className="quick-links"
      sx={{
        marginBottom: side ? "0" : "16px",
      }}
    >
      <Box
        className="quick-links__cards"
        sx={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          flexDirection: side ? "column" : "row",
        }}
      >
        {displayLinks.map((link) => (
          <QuickLinkCard key={link.label} {...link} />
        ))}
      </Box>
    </Box>
  );
};

export default QuickLinks;
